import React from 'react';
import {
  Gavel,
  Users,
  Undo2,
  Wallet,
  CircleCheck,
  BarChart3,
  ArrowUpRight,
  History,
  ListChecks,
  TrendingUp,
  Clock,
  PieChart,
  Building,
  FileText,
  FileSpreadsheet,
  Inbox,
  UserRoundPen,
  Landmark,
  Handshake,
  FolderTree,
  UserPlus,
} from 'lucide-react';
import '../../../../styles/components/client-management.scss';
import i18n from '../../../../i18n';
import { formatAmount, formatNumber } from '../../../../utils/common';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getClientDashboard } from './services/clientManagementService';
import { OMR } from '../../../common/icons';

const ICON_BY_KEY = {
  auctions: Gavel,
  enrollments: Users,
  refunds: Undo2,
  payments: Wallet,
  approvals: CircleCheck,
  reports: BarChart3,
};

const REPORT_ICONS = {
  enrollment: Users,
  refund: Undo2,
  auction: Gavel,
  payment: Wallet,
  financial: PieChart,
  client: Building,
  pdf: FileText,
  excel: FileSpreadsheet,
};

const DEFAULT_STATS = {
  auctions: { active: 0, previous: 0, activeAmount: 0, previousAmount: 0, delta: '' },
  enrollments: { pending: 0, approved: 0, pendingAmount: 0, approvedAmount: 0, delta: '' },
  refunds: { pending: 0, completed: 0, pendingAmount: 0, completedAmount: 0, delta: '' },
  payments: { pending: 0, approved: 0, pendingAmount: 0, approvedAmount: 0, delta: '' },
  approvals: { pending: 0, approved: 0, pendingAmount: 0, approvedAmount: 0 },
  revenue: { total: 0, delta: '' },
  pendingTotal: { count: 0, delta: '' },
};

export class ClientManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      organizationName: '',
      stats: DEFAULT_STATS,
      activities: [],
      isLoading: true,
      hasError: false,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.loadDashboard();
  }

  async loadDashboard() {
    this.setState({ isLoading: true, hasError: false });
    try {
      const response = await getClientDashboard();
      this.setState({
        organizationName: response.data.organization_name || '',
        stats: response.data.stats || DEFAULT_STATS,
        activities: response.data.activities || [],
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error(error.message || 'Failed to load dashboard data');
      this.setState({ hasError: true });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  greetingKey() {
    const hour = new Date().getHours();
    if (hour < 12) return 'good_morning';
    if (hour < 17) return 'good_afternoon';
    return 'good_evening';
  }

  get locale() {
    return i18n.language === 'ar' ? 'ar-OM-u-nu-arab' : 'en-US';
  }

  num = (value) => formatNumber(value ?? 0, this.locale);

  money = (value) => formatAmount(value ?? 0, this.locale);

  ratio = (pending, settled) => {
    const total = (Number(pending) || 0) + (Number(settled) || 0);
    if (!total) return 0;
    return Math.round(((Number(settled) || 0) / total) * 100);
  };

  go = (key) => {
    this.props.onNavigate?.(key);
  };

  // ---------- hero ----------

  renderHero() {
    const { organizationName } = this.state;

    const shortcuts = [
      {
        key: 'auctions',
        pathname: '/auctions-list',
        query: { status: 'active' },
        icon: Gavel,
        label: i18n.t('view_active_auctions'),
      },
      {
        key: 'enrollments',
        pathname: '/enrollments/list',
        query: { status: 'pending' },
        icon: Users,
        label: i18n.t('pending_enrollments'),
      },
      {
        key: 'refunds',
        pathname: '/enrollments/refund/list',
        query: { status: 'A' },
        icon: Undo2,
        label: i18n.t('pending_refunds'),
      },
      {
        key: 'payments',
        pathname: '/auction-payment',
        query: { status: 'pending' },
        icon: Wallet,
        label: i18n.t('pending_payments'),
      },
      // {
      //   key: 'reports',
      //   pathname: '/report',
      //   icon: BarChart3,
      //   label: i18n.t('reports'),
      // },
    ];

    return (
      <header className="cd-hero">
        <div className="cd-hero__deco" aria-hidden="true">
          <i />
          <i />
          <i />
          <b />
        </div>

        <p className="cd-hero__eyebrow">
          {i18n.t(this.greetingKey())}
          <span aria-hidden="true">
            <Handshake size={22} strokeWidth={2} aria-hidden="true" />
          </span>
        </p>

        <h1 className="cd-hero__title">
          {i18n.t('welcome_back')}, {organizationName || '--'}
        </h1>

        <p className="cd-hero__sub">{i18n.t('dashboard_hero_sub')}</p>

        <div className="cd-hero__actions">
          {shortcuts.map(({ key, pathname, query, icon: Icon, label }) => (
            <NavLink
              key={key}
              to={{
                pathname,
                search: new URLSearchParams(
                  Object.fromEntries(
                    Object.entries(query || {}).filter(
                      ([, v]) => v !== null && v !== '' && v !== undefined
                    )
                  )
                ).toString(),
              }}
              className="cd-chip"
            >
              <Icon size={15} strokeWidth={1.9} />
              {label}
            </NavLink>
          ))}
        </div>
      </header>
    );
  }

  // ---------- tab rail ----------

  renderTabs() {
    const { stats } = this.state;

    const tabs = [
      {
        key: 'dashboard',
        pathname: '/dashboard',
        icon: BarChart3,
        label: i18n.t('dashboard'),
      },
      {
        key: 'auctions',
        pathname: '/auctions-list',
        icon: Gavel,
        label: i18n.t('auctions'),
      },
      {
        key: 'enrollments',
        pathname: '/enrollments/list',
        icon: Users,
        label: i18n.t('enrollments'),
        count: stats.enrollments?.pending,
      },
      {
        key: 'refunds',
        pathname: '/enrollments/refund/list',
        icon: Undo2,
        label: i18n.t('refunds'),
        count: stats.refunds?.pending,
      },
      {
        key: 'approvals',
        pathname: '/auction_approval',
        icon: CircleCheck,
        label: i18n.t('auction_approval'),
        count: stats.approvals?.pending,
      },
      {
        key: 'payments',
        pathname: '/auction-payment',
        icon: Wallet,
        label: i18n.t('auction_payments'),
        count: stats.payments?.pending,
      },
      {
        key: 'profile',
        pathname: '/profile',
        icon: UserRoundPen,
        label: i18n.t('profile'),
      },
      {
        key: 'config',
        pathname: '/client-account-config',
        icon: Landmark,
        label: i18n.t('config'),
      },
    ];

    return (
      <nav className="cd-tabs" aria-label={i18n.t('dashboard_sections')}>
        {tabs.map(({ key, pathname, icon: Icon, label, count }) => (
          <NavLink
            key={key}
            to={{ pathname }}
            className={`cd-tab ${this.props.activeTab === key ? 'is-active' : ''}`}
            aria-current={this.props.activeTab === key ? 'page' : undefined}
          >
            <Icon size={15} strokeWidth={1.9} />
            {label}
            {count > 0 && <span className="cd-tab__count">{this.num(count)}</span>}
          </NavLink>
        ))}
      </nav>
    );
  }

  // ---------- executive summary ----------

  renderSummaryCard(card) {
    const Icon = ICON_BY_KEY[card.key];

    return (
      <article key={card.key} className="cd-card">
        {card.live ? (
          <span className="cd-card__live">
            {i18n.t('live')}
            <ArrowUpRight size={11} strokeWidth={2.4} />
          </span>
        ) : (
          <button
            type="button"
            className="cd-card__open"
            aria-label={`${i18n.t('open')} ${card.title}`}
            onClick={() => this.go(card.key)}
          >
            <ArrowUpRight size={15} strokeWidth={2.2} />
          </button>
        )}

        <div className={`cd-card__icon cd-card__icon--${card.tone}`}>
          <Icon size={20} strokeWidth={1.8} />
        </div>

        <h3 className="cd-card__title">{card.title}</h3>
        <p className="cd-card__caption">{card.caption}</p>

        <div className="cd-card__stats">
          {[card.left, card.right].map((stat, index) => (
            <NavLink
              key={stat.label}
              to={{
                pathname: stat.pathname,
                search: new URLSearchParams(
                  Object.fromEntries(
                    Object.entries(stat.query || {}).filter(
                      ([, v]) => v !== null && v !== '' && v !== undefined
                    )
                  )
                ).toString(),
              }}
              className={`cd-stat cd-stat--${card.tone}`}
            >
              <span className="cd-stat__label">{stat.label}</span>

              <div className="cd-stat__value">{this.num(stat.value)}</div>

              {stat.amount != null && (
                <div className="cd-stat__amount">
                  <OMR size={10} strokeWidth={2} />
                  {this.money(stat.amount)}
                </div>
              )}

              <ArrowUpRight className="cd-stat__arrow" size={16} strokeWidth={2} />
            </NavLink>
          ))}
        </div>

        <div className={`cd-card__bar cd-card__bar--${card.tone}`} aria-hidden="true">
          <i style={{ width: `${this.ratio(card.left.value, card.right.value)}%` }} />
        </div>
      </article>
    );
  }

  renderSummary() {
    const s = this.state.stats;

    const cards = [
      {
        key: 'auctions',
        tone: 'teal',
        live: true,
        title: i18n.t('mzadcom_auctions'),
        caption: i18n.t('live_marketplace'),
        left: {
          label: i18n.t('active'),
          value: s.auctions?.active,
          amount: s.auctions?.activeAmount,
          pathname: '/auctions-list',
          query: {
            type: 'active',
          },
        },
        right: {
          label: i18n.t('previous'),
          value: s.auctions?.previous,
          amount: s.auctions?.previousAmount,
          pathname: '/auctions-list',
          query: {
            type: 'previous',
          },
        },
      },
      {
        key: 'enrollments',
        tone: 'olive',
        title: i18n.t('auction_enrollment'),
        caption: i18n.t('bidder_onboarding'),
        left: {
          label: i18n.t('pending'),
          value: s.enrollments?.pending,
          amount: s.enrollments?.pendingAmount,
          pathname: '/enrollments/list',
          query: {
            status: 'pending_client',
          },
        },
        right: {
          label: i18n.t('approved'),
          value: s.enrollments?.approved,
          amount: s.enrollments?.approvedAmount,
          pathname: '/enrollments/list',
          query: {
            status: 'approved',
          },
        },
      },
      {
        key: 'refunds',
        tone: 'orange',
        title: i18n.t('enrollment_refund'),
        caption: i18n.t('bidder_reimbursement'),
        left: {
          label: i18n.t('pending'),
          value: s.refunds?.pending,
          amount: s.refunds?.pendingAmount,
          pathname: '/enrollments/refund/list',
          query: {
            status: 'pending_client',
          },
        },
        right: {
          label: i18n.t('completed'),
          value: s.refunds?.completed,
          amount: s.refunds?.completedAmount,
          pathname: '/enrollments/refund/list',
          query: {
            status: 'approved',
          },
        },
      },
      {
        key: 'approvals',
        tone: 'teal',
        title: i18n.t('auction_approval'),
        caption: i18n.t('review_pipeline'),
        left: {
          label: i18n.t('pending'),
          value: s.approvals?.pending,
          amount: s.approvals?.pendingAmount,
          pathname: '/auction_approval',
          query: {
            status: 'pending',
          },
        },
        right: {
          label: i18n.t('approved'),
          value: s.approvals?.approved,
          amount: s.approvals?.approvedAmount,
          pathname: '/auction_approval',
          query: {
            status: 'approved',
          },
        },
      },
      {
        key: 'payments',
        tone: 'green',
        title: i18n.t('auction_payments'),
        caption: i18n.t('settlement_tracking'),
        left: {
          label: i18n.t('pending'),
          value: s.payments?.pending,
          amount: s.payments?.pendingAmount,
          pathname: '/auction-payment',
          query: {
            status: 'pending',
          },
        },
        right: {
          label: i18n.t('approved'),
          value: s.payments?.approved,
          amount: s.payments?.approvedAmount,
          pathname: '/auction-payment',
          query: {
            status: 'completed',
          },
        },
      },
    ];

    return (
      <>
        <h2 className="cd-section-head">{i18n.t('executive_summary')}</h2>
        <div className="cd-grid">{cards.map((card) => this.renderSummaryCard(card))}</div>
      </>
    );
  }

  // ---------- activity feed ----------

  activityVisual(title = '') {
    const t = title.toLowerCase();

    if (t.includes('login') || t.includes('logout')) {
      return { icon: Users, tone: 'ink' };
    }

    if (t.includes('verify')) {
      return { icon: CircleCheck, tone: 'green' };
    }

    if (t.includes('register')) {
      return { icon: UserPlus, tone: 'green' };
    }

    if (t.includes('wallet')) {
      return { icon: Wallet, tone: 'green' };
    }

    if (t.includes('payment')) {
      return { icon: Wallet, tone: 'green' };
    }

    if (t.includes('refund')) {
      return { icon: Undo2, tone: 'orange' };
    }

    if (t.includes('enroll')) {
      return { icon: Users, tone: 'olive' };
    }

    if (t.includes('auction')) {
      return { icon: Gavel, tone: 'teal' };
    }

    if (t.includes('approv')) {
      return { icon: CircleCheck, tone: 'teal' };
    }

    if (t.includes('group')) {
      return { icon: FolderTree, tone: 'blue' };
    }

    return { icon: BarChart3, tone: 'ink' };
  }
  renderActivity() {
    const { activities } = this.state;

    return (
      <section className="cd-panel">
        <h2 className="cd-panel__head">
          <History size={17} strokeWidth={1.9} />
          {i18n.t('recent_activities')}
        </h2>

        {activities.length === 0 ? (
          <div className="cd-empty">
            <Inbox size={34} strokeWidth={1.3} />
            <p>{i18n.t('no_recent_activity')}</p>
          </div>
        ) : (
          <ul className="cd-feed">
            {activities.map((item) => {
              const { icon: Icon, tone } = this.activityVisual(item.title);

              return (
                <li key={item.id} className="cd-feed__item">
                  <span className={`cd-feed__dot cd-feed__dot--${item.tone || tone}`}>
                    <Icon size={15} strokeWidth={1.9} />
                  </span>
                  <div className="cd-feed__body">
                    <p
                      className={`cd-feed__title ${item.highlight ? 'cd-feed__title--accent' : ''}`}
                    >
                      {item.title}
                    </p>
                    <p className="cd-feed__meta">
                      {item.actor} · {item.when}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    );
  }

  // ---------- pending action center ----------

  renderPendingActions() {
    const s = this.state.stats;

    const rows = [
      {
        key: 'enrollments',
        icon: Users,
        title: i18n.t('pending_enrollment'),
        count: s.enrollments?.pending,
        pathname: '/enrollments/list',
        query: {
          status: 'pending',
        },
      },
      {
        key: 'refunds',
        icon: Undo2,
        title: i18n.t('pending_refund'),
        count: s.refunds?.pending,
        pathname: '/enrollments/refund/list',
        query: {
          status: 'pending_client',
        },
      },
      {
        key: 'approvals',
        icon: CircleCheck,
        title: i18n.t('pending_auction_approval'),
        count: s.approvals?.pending,
        pathname: '/auction_approval',
        query: {
          status: 'pending',
        },
      },
      {
        key: 'payments',
        icon: Wallet,
        title: i18n.t('pending_payments'),
        count: s.payments?.pending,
        pathname: '/auction-enrollments',
        query: {
          status: 'pending',
        },
      },
    ].filter((row) => row.count > 0);

    return (
      <section className="cd-panel">
        <h2 className="cd-panel__head">
          <ListChecks size={17} strokeWidth={1.9} />
          {i18n.t('pending_action_center')}
        </h2>

        {rows.length === 0 ? (
          <div className="cd-empty">
            <CircleCheck size={34} strokeWidth={1.3} />
            <p>{i18n.t('nothing_needs_review')}</p>
          </div>
        ) : (
          rows.map(({ key, icon: Icon, title, count, pathname, query }) => (
            <div key={key} className="cd-action">
              <span className="cd-action__icon">
                <Icon size={17} strokeWidth={1.9} />
              </span>
              <div className="cd-action__body">
                <p className="cd-action__title">{title}</p>
                <p className="cd-action__hint">
                  {this.num(count)} {i18n.t('require_attention')}
                </p>
              </div>
              <NavLink
                to={{
                  pathname,
                  search: new URLSearchParams(
                    Object.fromEntries(
                      Object.entries(query || {}).filter(
                        ([, v]) => v !== null && v !== '' && v !== undefined
                      )
                    )
                  ).toString(),
                }}
                className="cd-action__btn"
              >
                {i18n.t('review')}
              </NavLink>
            </div>
          ))
        )}
      </section>
    );
  }

  // ---------- performance ----------

  renderPerformance() {
    const s = this.state.stats;

    const metrics = [
      {
        key: 'active_auctions',
        icon: Gavel,
        value: this.num(s.auctions?.active),
        label: i18n.t('total_active_auctions'),
        delta: s.auctions?.delta,
      },
      {
        key: 'enrollment_amount',
        icon: Users,
        value: this.money(s.enrollments?.approvedAmount),
        label: i18n.t('approved_enrollment_amount'),
        delta: s.enrollments?.delta,
      },
      {
        key: 'refund_amount',
        icon: Undo2,
        value: this.money(s.refunds?.completedAmount),
        label: i18n.t('total_refunded_amount'),
        delta: s.refunds?.delta,
      },
      {
        key: 'revenue',
        icon: TrendingUp,
        value: this.money(s.revenue?.total),
        label: i18n.t('total_auction_revenue'),
        delta: s.revenue?.delta,
      },
      {
        key: 'payment_amount',
        icon: Wallet,
        value: this.money(s.payments?.approvedAmount),
        label: i18n.t('total_approved_payments'),
        delta: s.payments?.delta,
      },
      {
        key: 'pending_total',
        icon: Clock,
        value: this.num(s.pendingTotal?.count),
        label: i18n.t('total_pending_requests'),
        delta: s.pendingTotal?.delta,
      },
    ];

    return (
      <>
        <h2 className="cd-section-head">{i18n.t('performance_overview')}</h2>

        <div className="cd-grid">
          {metrics.map(({ key, icon: Icon, value, label, delta }) => {
            const isDown = typeof delta === 'string' && delta.trim().startsWith('-');

            return (
              <article key={key} className="cd-metric">
                <div className="cd-metric__icon">
                  <Icon size={17} strokeWidth={1.9} />
                </div>
                <div className="cd-metric__value">{value}</div>
                <p className="cd-metric__label">{label}</p>
                {delta && (
                  <span className={`cd-metric__delta ${isDown ? 'cd-metric__delta--down' : ''}`}>
                    {delta}
                  </span>
                )}
              </article>
            );
          })}
        </div>
      </>
    );
  }

  // ---------- reports shortcut ----------

  renderReportsShortcut() {
    const reports = [
      { key: 'enrollment', label: i18n.t('enrollment_report') },
      { key: 'refund', label: i18n.t('refund_report') },
      { key: 'auction', label: i18n.t('auction_report') },
      { key: 'payment', label: i18n.t('payment_report') },
      { key: 'financial', label: i18n.t('financial_summary') },
      { key: 'client', label: i18n.t('client_summary') },
      { key: 'pdf', label: i18n.t('export_pdf') },
      { key: 'excel', label: i18n.t('export_excel') },
    ];

    return (
      <section className="cd-reports">
        <h2 className="cd-panel__head">
          <BarChart3 size={17} strokeWidth={1.9} />
          {i18n.t('reports_shortcut')}
        </h2>

        <div className="cd-reports__grid">
          {reports.map(({ key, label }) => {
            const Icon = REPORT_ICONS[key];

            return (
              <button
                key={key}
                type="button"
                className="cd-report"
                onClick={() => this.props.onReport?.(key)}
              >
                <Icon size={20} strokeWidth={1.8} />
                {label}
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  // ---------- skeleton loading ----------
  // Mirrors the real layout section-for-section so nothing "jumps" once data
  // arrives. All classes live under the `cd-skel-` namespace so they never
  // collide with the live `cd-*` component classes above.

  renderLoading() {
    const heroChips = Array.from({ length: 5 });
    const tabs = Array.from({ length: 8 });
    const summaryCards = Array.from({ length: 5 });
    const feedItems = Array.from({ length: 4 });
    const actionRows = Array.from({ length: 3 });
    const metrics = Array.from({ length: 6 });
    const reportTiles = Array.from({ length: 8 });

    return (
      <div className="cd-page" aria-busy="true" aria-label={i18n.t('loading_dashboard')}>
        <div className="cd-shell">
          {/* breadcrumb */}
          <div className="cd-skel-crumb">
            <span className="cd-skel-bit cd-skel-crumb__bit" />
            <span className="cd-skel-bit cd-skel-crumb__bit cd-skel-crumb__bit--current" />
          </div>

          {/* hero */}
          <div className="cd-skel-hero">
            <span className="cd-skel-bit cd-skel-hero__eyebrow" />
            <span className="cd-skel-bit cd-skel-hero__title" />
            <span className="cd-skel-bit cd-skel-hero__sub" />
            <div className="cd-skel-hero__actions">
              {heroChips.map((_, i) => (
                <span key={i} className="cd-skel-bit cd-skel-hero__chip" />
              ))}
            </div>
          </div>

          {/* tab rail */}
          <div className="cd-skel-tabs">
            {tabs.map((_, i) => (
              <span key={i} className="cd-skel-bit cd-skel-tab" />
            ))}
          </div>

          {/* executive summary */}
          <span className="cd-skel-bit cd-skel-section-head" />
          <div className="cd-skel-grid">
            {summaryCards.map((_, i) => (
              <div key={i} className="cd-skel-card">
                <span className="cd-skel-bit cd-skel-card__icon" />
                <span className="cd-skel-bit cd-skel-card__title" />
                <span className="cd-skel-bit cd-skel-card__caption" />
                <div className="cd-skel-card__stats">
                  <div className="cd-skel-card__stat">
                    <span className="cd-skel-bit cd-skel-card__stat-label" />
                    <span className="cd-skel-bit cd-skel-card__stat-value" />
                  </div>
                  <div className="cd-skel-card__stat">
                    <span className="cd-skel-bit cd-skel-card__stat-label" />
                    <span className="cd-skel-bit cd-skel-card__stat-value" />
                  </div>
                </div>
                <span className="cd-skel-bit cd-skel-card__bar" />
              </div>
            ))}
          </div>

          {/* activity + pending actions */}
          <div className="cd-skel-split">
            <div className="cd-skel-panel">
              <span className="cd-skel-bit cd-skel-panel__head" />
              {feedItems.map((_, i) => (
                <div key={i} className="cd-skel-feed-row">
                  <span className="cd-skel-bit cd-skel-feed-row__dot" />
                  <div className="cd-skel-feed-row__body">
                    <span className="cd-skel-bit cd-skel-feed-row__title" />
                    <span className="cd-skel-bit cd-skel-feed-row__meta" />
                  </div>
                </div>
              ))}
            </div>

            <div className="cd-skel-panel">
              <span className="cd-skel-bit cd-skel-panel__head" />
              {actionRows.map((_, i) => (
                <div key={i} className="cd-skel-action-row">
                  <span className="cd-skel-bit cd-skel-action-row__icon" />
                  <div className="cd-skel-action-row__body">
                    <span className="cd-skel-bit cd-skel-action-row__title" />
                    <span className="cd-skel-bit cd-skel-action-row__hint" />
                  </div>
                  <span className="cd-skel-bit cd-skel-action-row__btn" />
                </div>
              ))}
            </div>
          </div>

          {/* performance overview */}
          <span className="cd-skel-bit cd-skel-section-head" />
          <div className="cd-skel-metric-grid">
            {metrics.map((_, i) => (
              <div key={i} className="cd-skel-metric">
                <span className="cd-skel-bit cd-skel-metric__icon" />
                <span className="cd-skel-bit cd-skel-metric__value" />
                <span className="cd-skel-bit cd-skel-metric__label" />
              </div>
            ))}
          </div>

          {/* reports shortcut */}
          <div className="cd-skel-reports">
            <span className="cd-skel-bit cd-skel-panel__head" />
            <div className="cd-skel-reports__grid">
              {reportTiles.map((_, i) => (
                <span key={i} className="cd-skel-bit cd-skel-report" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isLoading } = this.state;

    if (isLoading) {
      return this.renderLoading();
    }

    return (
      <div className="cd-page">
        <div className="cd-shell">
          <p className="cd-crumb">
            <NavLink to="/">{i18n.t('home')}</NavLink>
            <span className="cd-crumb__current">{i18n.t('dashboard')}</span>
          </p>

          {this.renderHero()}
          {this.renderTabs()}
          {this.renderSummary()}

          <div className="cd-split">
            {this.renderActivity()}
            {this.renderPendingActions()}
          </div>

          {this.renderPerformance()}
          {/* {this.renderReportsShortcut()} */}
        </div>
      </div>
    );
  }
}

ClientManagement.defaultProps = {
  activeTab: 'dashboard',
};
